import { useEffect, useRef } from 'react'
import { useVersioning } from '../../versioning'
import { useIsPreviewView } from '../../shares/views/PreviewView/PreviewContext'

/**
 * useAnnotationVersionBlocking
 *
 * This hook returns true when:
 * - The user sees annotations on the latest version
 * - The user sees annotations on a once latest version while a new version is already pushed (maybe work in progress, so we don't disturb)
 * - The user sees annotations on a Preview
 *
 * This hook returns false when:
 * - The user opens a older version without having seen it previously
 * - The user navigates from the latest to an older version
 */
export const useAnnotationVersionBlocking = () => {
  const isPreview = useIsPreviewView()
  const { versionShortId, latestVersion } = useVersioning()

  // Call all hooks unconditionally at the top level
  const versionWhenMounting = useRef(versionShortId)
  const previousLatestVersion = useRef(latestVersion)

  useEffect(() => {
    // Early return if no versionShortId
    if (!versionShortId || isPreview) return

    /**
     * Reset the "versionWhenMounting" when the user accesses the
     * latest version available
     */
    if (versionShortId === latestVersion.shortId) {
      versionWhenMounting.current = versionShortId
      previousLatestVersion.current = latestVersion
    }
  }, [latestVersion, versionShortId, isPreview])

  if (isPreview) {
    return true
  }

  // Early return after all hooks have been called
  if (!versionShortId) {
    return false
  }

  return versionShortId === previousLatestVersion.current.shortId
}
